import { useCallback, useRef } from 'react'

type Debounce = (callback: () => void) => void

/**
 * Leading edge debounce hook - executes immediately on first call, then debounces subsequent calls
 * @param timeout milliseconds
 */
export const useLeadingDebounce = (timeout: number): Debounce => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasExecuted = useRef<boolean>(false)

  const debounce: Debounce = useCallback(
    (callback) => {
      const callNow = !timer.current && !hasExecuted.current

      if (timer.current) {
        clearTimeout(timer.current)
      }

      timer.current = setTimeout(() => {
        timer.current = null
        hasExecuted.current = false
      }, timeout)

      if (callNow) {
        hasExecuted.current = true
        callback()
      }
    },
    [timeout]
  )

  return debounce
}
